<script setup lang="ts">
import { defineRule, Field as VeeField } from 'vee-validate'

import { requiredValidator } from '@/utils/rulesOfValidate'

import { useBasketPointStore } from '@/store/basket/basket-point'

import { DeliveryMethods } from '@/api/modules/basket/types'
import { BasketPoint } from '@/types/api/api-v3/basket-points'

import BasketAddressList from '@/components/Basket/basket-addresses/BasketAddressList.vue'
const BasketFailed = defineAsyncComponent(
  () => import('@/components/Basket/basket-utils/BasketFailed.vue'),
)

type Props = {
  effectShadow?: boolean
  selectedAddress?: BasketPoint
  pickupPoints: BasketPoint[]
  maxCountOfVisibleAddresses: number
  listWithAnimation?: boolean
  withoutValidate?: boolean
}
const props = defineProps<Props>()

const emit = defineEmits([
  'delivery-method-changed',
  'set-selected-address',
  'open-dialog-redirect-city',
])

const emitDeliveryMethodChanged = () => {
  emit('delivery-method-changed')
}

const pointStore = useBasketPointStore()

const setSelectedAddress = (value: BasketPoint) => {
  emit('set-selected-address', {
    point_id: value.id,
    delivery_method: DeliveryMethods.PICKUP,
    order_address: value.address,
  })
}

const loadPoints = async () => {
  await pointStore.getPoints()
}

const requiredAddressValidator = (value: string) => {
  if (requiredValidator(value) !== true) {
    return 'Выберите адрес'
  }

  return true
}

defineRule('required-address', requiredAddressValidator)

const validateRules = computed(() =>
  props.withoutValidate ? '' : 'required-address',
)

const onOpenDialogRedirectCity = (basketPoint: BasketPoint) =>
  emit('open-dialog-redirect-city', basketPoint)
</script>

<template>
  <div id="point_id" class="basket-pickup">
    <the-loader
      v-if="pointStore.isLoading"
      size="100"
      variant="circular"
      color="var(--ui-primary)"
    />

    <basket-failed
      v-else-if="pointStore.error"
      error-message="Не удалось загрузить список точек самовывоза"
      @update="loadPoints"
    />

    <vee-field
      v-else
      v-slot="{ errors, field }"
      :rules="validateRules"
      name="point_id"
      :model-value="selectedAddress"
    >
      <basket-address-list
        v-bind="field"
        :effect-shadow="effectShadow"
        :model-value="selectedAddress"
        :delivery-method="DeliveryMethods.PICKUP"
        :addresses="pickupPoints"
        :max-count-of-visible-addresses="maxCountOfVisibleAddresses"
        :list-with-animation="listWithAnimation"
        @update:model-value="setSelectedAddress"
        @delivery-method-changed="emitDeliveryMethodChanged"
        @open-dialog-redirect-city="onOpenDialogRedirectCity"
      />

      <basket-failed
        v-if="Object.keys(errors).length"
        :error-message="errors[0]"
        without-button
      />
    </vee-field>
  </div>
</template>

<style lang="scss" scoped>
.basket-pickup {
  .app-loader {
    position: relative;
  }
}
</style>
